import { Injectable, Injector, inject } from '@angular/core';
import { switchMap, of, Observable } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { LoyaltyCodesForProductView } from '@src/api';

import { LoyaltyProgramProduct } from '../models';
import { DiscountDialogComponent, DiscountGroupsDialogComponent } from '../lp-view-product/components';
import { LoyaltyProgramProductWithExternalUnionId } from '../models/LoyaltyProgramProduct';
import { LoyaltyProgramDiscountDialogData } from '../lp-view-product/components/discount-dialog/types';

import { LoyaltyProgramApiService } from './loyalty-program-api.service';

@Injectable({
  providedIn: 'root',
})
export class LpDiscountDialogService {
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly loyaltyProgramApiService = inject(LoyaltyProgramApiService);

  openDiscountGroupsDialog(product: LoyaltyProgramProduct, externalUnionId?: string) {
    const data: LoyaltyProgramProductWithExternalUnionId = {
      product,
      externalUnionId,
    };

    return this.dialogService.open<string>(new PolymorpheusComponent(DiscountGroupsDialogComponent, this.injector), {
      label: this.translateService.instant('components.loyaltyProgram.dialogs.discountGroupsHeader'),
      data,
      size: 'l',
    });
  }

  openDiscountDialogWithRequest(
    product: LoyaltyProgramProduct,
    externalUnionId?: string,
    discountGroupId?: string,
  ): Observable<void | null> {
    if (!product?.id) return of(null);

    const selectedDiscountGroup = discountGroupId
      ? product.terms.find(term => term.id === discountGroupId)
      : product.terms[0];

    if (!selectedDiscountGroup?.id) return of(null);

    return this.loyaltyProgramApiService.getProductCode(selectedDiscountGroup.id, externalUnionId).pipe(
      switchMap(groupCode => {
        if (groupCode) {
          if (selectedDiscountGroup) {
            selectedDiscountGroup.promoCode = groupCode.discountCode ?? '';
            selectedDiscountGroup.description = groupCode.discountOptions ?? '';
          }
        }

        const data: LoyaltyProgramDiscountDialogData = {
          onlyCode: false,
          code: groupCode.discountCode,
          discountOptions: groupCode.discountOptions,
          discountQRCodeFileId: groupCode.discountQRCodeFileId,
          validSince: groupCode.validSince,
          validTill: groupCode.validTill,
          productId: groupCode.productId,
          generateQrcode: groupCode.generateQrcode,
          generateBarcode: groupCode.generateBarcode,

          unionId: product.unionId,
        };

        return this.openDiscountDialog(data);
      }),
    );
  }

  openDiscountDialogByCode(codeData: LoyaltyCodesForProductView) {
    const data: LoyaltyProgramDiscountDialogData = {
      onlyCode: true,
      code: codeData.code,
      discountOptions: codeData.discountOptions,
      discountQRCodeFileId: codeData.discountQRCodeFileId,
      validSince: codeData.validSince,
      validTill: codeData.validTill,
      productId: codeData.productId,
      generateBarcode: codeData.generateBarcode,
      generateQrcode: codeData.generateQrcode,

      unionId: codeData.isAdvertisment ? codeData.parentOrganisationId : undefined,
    };

    this.openDiscountDialog(data).subscribe();
  }

  private openDiscountDialog(data: LoyaltyProgramDiscountDialogData) {
    return this.dialogService.open(new PolymorpheusComponent(DiscountDialogComponent, this.injector), {
      label: this.translateService.instant('components.loyaltyProgram.dialogs.discountCodeHeader'),
      data,
    });
  }
}
